export interface IMcsaMeshConfig {
    colors: string[];
    distortion: number;
    swirl: number;
    grainMixer: number;
    grainOverlay: number;
    speed: number;
    offsetX: number;
}

export const MCSA_WELCOME_MESH: IMcsaMeshConfig = {
    colors: ['#FFE4B5', '#FFB6D9', '#DDA0DD', '#F8F0FF', '#FFF8F0', '#FFF0F5'],
    distortion: 0.6,
    swirl: 0.4,
    grainMixer: 0,
    grainOverlay: 0,
    speed: 0.3,
    offsetX: 0.05,
};

const VERTEX_SHADER = `#version 300 es
precision mediump float;
layout(location = 0) in vec4 a_position;
uniform vec2 u_resolution;
uniform float u_pixelRatio;
uniform float u_imageAspectRatio;
uniform float u_originX;
uniform float u_originY;
uniform float u_worldWidth;
uniform float u_worldHeight;
uniform float u_fit;
uniform float u_scale;
uniform float u_rotation;
uniform float u_offsetX;
uniform float u_offsetY;
out vec2 v_objectUV;
vec3 getBoxSize(float boxRatio, vec2 givenBoxSize) {
  vec2 box = vec2(0.);
  box.x = boxRatio * min(givenBoxSize.x / boxRatio, givenBoxSize.y);
  if (u_fit == 1.) {
    box.x = boxRatio * min(u_resolution.x / boxRatio, u_resolution.y);
  } else if (u_fit == 2.) {
    box.x = boxRatio * max(u_resolution.x / boxRatio, u_resolution.y);
  }
  box.y = box.x / boxRatio;
  return vec3(box, 0.);
}
void main() {
  gl_Position = a_position;
  vec2 uv = gl_Position.xy * .5;
  vec2 boxOrigin = vec2(.5 - u_originX, u_originY - .5);
  vec2 givenBoxSize = vec2(u_worldWidth, u_worldHeight);
  givenBoxSize = max(givenBoxSize, vec2(1.)) * u_pixelRatio;
  float r = u_rotation * 3.14159265358979323846 / 180.;
  mat2 graphicRotation = mat2(cos(r), sin(r), -sin(r), cos(r));
  vec2 graphicOffset = vec2(-u_offsetX, u_offsetY);
  vec2 fixedRatioBoxGivenSize = vec2(
    (u_worldWidth == 0.) ? u_resolution.x : givenBoxSize.x,
    (u_worldHeight == 0.) ? u_resolution.y : givenBoxSize.y
  );
  vec2 objectBoxSize = getBoxSize(1., fixedRatioBoxGivenSize).xy;
  vec2 objectWorldScale = u_resolution.xy / objectBoxSize;
  v_objectUV = uv;
  v_objectUV *= objectWorldScale;
  v_objectUV += boxOrigin * (objectWorldScale - 1.);
  v_objectUV += graphicOffset;
  v_objectUV /= u_scale;
  v_objectUV = graphicRotation * v_objectUV;
}`;

const FRAGMENT_SHADER = `#version 300 es
precision mediump float;
uniform float u_time;
uniform vec4 u_colors[10];
uniform float u_colorsCount;
uniform float u_distortion;
uniform float u_swirl;
uniform float u_grainMixer;
uniform float u_grainOverlay;
in vec2 v_objectUV;
out vec4 fragColor;
#define TWO_PI 6.28318530718
#define PI 3.14159265358979323846
vec2 rotate(vec2 uv, float th) {
  return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
}
float hash21(vec2 p) {
  p = fract(p * vec2(0.3183099, 0.3678794)) + 0.1;
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}
float valueNoise(vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  float x1 = mix(a, b, u.x);
  float x2 = mix(c, d, u.x);
  return mix(x1, x2, u.y);
}
float noise(vec2 n, vec2 seedOffset) {
  return valueNoise(n + seedOffset);
}
vec2 getPosition(int i, float t) {
  float a = float(i) * .37;
  float b = .6 + fract(float(i) / 3.) * .9;
  float c = .8 + fract(float(i + 1) / 4.);
  float x = sin(t * b + a);
  float y = cos(t * c + a * 1.5);
  return .5 + .5 * vec2(x, y);
}
void main() {
  vec2 uv = v_objectUV;
  uv += .5;
  vec2 grainUV = uv * 1000.;
  float grain = noise(grainUV, vec2(0.));
  float mixerGrain = .4 * u_grainMixer * (grain - .5);
  const float firstFrameOffset = 41.5;
  float t = .5 * (u_time + firstFrameOffset);
  float radius = smoothstep(0., 1., length(uv - .5));
  float center = 1. - radius;
  for (float i = 1.; i <= 2.; i++) {
    uv.x += u_distortion * center / i * sin(t + i * .4 * smoothstep(.0, 1., uv.y)) * cos(.2 * t + i * 2.4 * smoothstep(.0, 1., uv.y));
    uv.y += u_distortion * center / i * cos(t + i * 2. * smoothstep(.0, 1., uv.x));
  }
  vec2 uvRotated = uv;
  uvRotated -= vec2(.5);
  float angle = 3. * u_swirl * radius;
  uvRotated = rotate(uvRotated, -angle);
  uvRotated += vec2(.5);
  vec3 color = vec3(0.);
  float opacity = 0.;
  float totalWeight = 0.;
  for (int i = 0; i < 10; i++) {
    if (i >= int(u_colorsCount)) break;
    vec2 pos = getPosition(i, t) + mixerGrain;
    vec3 colorFraction = u_colors[i].rgb * u_colors[i].a;
    float opacityFraction = u_colors[i].a;
    float dist = length(uvRotated - pos);
    dist = pow(dist, 3.5);
    float weight = 1. / (dist + 1e-3);
    color += colorFraction * weight;
    opacity += opacityFraction * weight;
    totalWeight += weight;
  }
  color /= max(1e-4, totalWeight);
  opacity /= max(1e-4, totalWeight);
  float grainOverlay = valueNoise(rotate(grainUV, 1.) + vec2(3.));
  grainOverlay = mix(grainOverlay, valueNoise(rotate(grainUV, 2.) + vec2(-1.)), .5);
  grainOverlay = pow(grainOverlay, 1.3);
  float grainOverlayV = grainOverlay * 2. - 1.;
  vec3 grainOverlayColor = vec3(step(0., grainOverlayV));
  float grainOverlayStrength = u_grainOverlay * abs(grainOverlayV);
  grainOverlayStrength = pow(grainOverlayStrength, .8);
  color = mix(color, grainOverlayColor, .35 * grainOverlayStrength);
  opacity += .5 * grainOverlayStrength;
  opacity = clamp(opacity, 0., 1.);
  fragColor = vec4(color, opacity);
}`;

const hexToRgba = (hex: string): [number, number, number, number] => {
    const n = hex.replace('#', '');
    return [
        parseInt(n.slice(0, 2), 16) / 255,
        parseInt(n.slice(2, 4), 16) / 255,
        parseInt(n.slice(4, 6), 16) / 255,
        1,
    ];
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.warn(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }

    return shader;
};

export const mountMcsaMesh = (
    root: HTMLElement,
    fallback: HTMLElement | null,
    config: IMcsaMeshConfig,
): (() => void) => {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2', {
        alpha: true,
        antialias: true,
        premultipliedAlpha: true,
    });

    if (!gl) return () => undefined;

    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertexShader || !fragmentShader) return () => undefined;

    const program = gl.createProgram();
    if (!program) return () => undefined;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.warn(gl.getProgramInfoLog(program));
        return () => undefined;
    }

    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const loc = (name: string) => gl.getUniformLocation(program, name);
    const colorData = new Float32Array(40);
    config.colors.forEach((hex, i) => {
        colorData.set(hexToRgba(hex), i * 4);
    });

    gl.uniform4fv(loc('u_colors'), colorData);
    gl.uniform1f(loc('u_colorsCount'), config.colors.length);
    gl.uniform1f(loc('u_distortion'), config.distortion);
    gl.uniform1f(loc('u_swirl'), config.swirl);
    gl.uniform1f(loc('u_grainMixer'), config.grainMixer);
    gl.uniform1f(loc('u_grainOverlay'), config.grainOverlay);
    gl.uniform1f(loc('u_originX'), 0.5);
    gl.uniform1f(loc('u_originY'), 0.5);
    gl.uniform1f(loc('u_worldWidth'), 0);
    gl.uniform1f(loc('u_worldHeight'), 0);
    gl.uniform1f(loc('u_fit'), 2);
    gl.uniform1f(loc('u_scale'), 1);
    gl.uniform1f(loc('u_rotation'), 0);
    gl.uniform1f(loc('u_offsetX'), config.offsetX);
    gl.uniform1f(loc('u_offsetY'), 0);
    gl.uniform1f(loc('u_imageAspectRatio'), 16 / 9);

    root.prepend(canvas);
    if (fallback) fallback.style.display = 'none';

    const MAX_PIXEL_COUNT = 1920 * 1080 * 4;
    const MIN_PIXEL_RATIO = 2;
    let time = 0;
    let last = performance.now();
    let frameId = 0;
    let disposed = false;

    const resize = () => {
        const dpr = Math.max(window.devicePixelRatio || 1, MIN_PIXEL_RATIO);
        let width = root.clientWidth * dpr;
        let height = root.clientHeight * dpr;
        const pixels = width * height;

        if (pixels > MAX_PIXEL_COUNT) {
            const scale = Math.sqrt(MAX_PIXEL_COUNT / pixels);
            width *= scale;
            height *= scale;
        }

        canvas.width = Math.max(1, Math.floor(width));
        canvas.height = Math.max(1, Math.floor(height));
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(loc('u_resolution'), canvas.width, canvas.height);
        gl.uniform1f(loc('u_pixelRatio'), dpr);
        gl.uniform1f(loc('u_imageAspectRatio'), canvas.width / canvas.height);
    };

    const render = (now: number) => {
        if (disposed) return;
        const delta = (now - last) / 1000;
        last = now;
        time += delta * config.speed;
        gl.uniform1f(loc('u_time'), time);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        frameId = requestAnimationFrame(render);
    };

    resize();
    window.addEventListener('resize', resize);
    frameId = requestAnimationFrame(render);

    return () => {
        disposed = true;
        cancelAnimationFrame(frameId);
        window.removeEventListener('resize', resize);
        canvas.remove();
        if (fallback) fallback.style.display = '';
    };
};
